import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';
import { authHeaders } from '../../services/auth';
import DataTable from '../../components/ui/DataTable';
import Spinner from '../../components/ui/Spinner';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import ImageUpload from '../../components/admin/ImageUpload';
import { adminTheme } from '../../theme-admin';
import { cache } from '../../utils/cache';

// Page Layout
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.xl};
  animation: slideIn 0.3s ease-out;
`;

const Title = styled.h2`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes['3xl']};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
`;

const CreateButton = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  background: ${adminTheme.gradients.purple};
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.bold};
  font-size: ${adminTheme.fontSizes.base};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  box-shadow: ${adminTheme.shadows.md};
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-2px);
    box-shadow: ${adminTheme.shadows.lg};
  }
`;

const TableWrapper = styled.div`
  background: ${adminTheme.colors.background.primary};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
`;

const ActionButton = styled.button`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[700]};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled(ActionButton)`
  border-color: ${adminTheme.colors.danger[300]};
  background: ${adminTheme.colors.danger[500]};
  color: white;

  &:hover {
    background: ${adminTheme.colors.danger[600]};
  }
`;

// Tab Navigation
const TabNav = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.xs};
  border-bottom: 2px solid ${adminTheme.colors.gray[200]};
  margin-bottom: ${adminTheme.spacing.lg};
`;

const Tab = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.lg};
  border: none;
  background: ${props => props.active ? adminTheme.colors.primary[50] : 'transparent'};
  color: ${props => props.active ? adminTheme.colors.primary[600] : adminTheme.colors.gray[600]};
  border-bottom: 3px solid ${props => props.active ? adminTheme.colors.primary[600] : 'transparent'};
  font-weight: ${props => props.active ? adminTheme.fontWeights.bold : adminTheme.fontWeights.medium};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  font-size: ${adminTheme.fontSizes.base};
  margin-bottom: -2px;

  &:hover {
    background: ${adminTheme.colors.primary[50]};
    color: ${adminTheme.colors.primary[600]};
  }
`;

// Form Components
const ModalForm = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[700]};
`;

const Input = styled.input`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const TextArea = styled.textarea`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  min-height: 100px;
  resize: vertical;
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${adminTheme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ThreeColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${adminTheme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};
  
  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
`;

// Curriculum Builder Components
const CurriculumBuilder = styled.div`
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  padding: ${adminTheme.spacing.lg};
  background: ${adminTheme.colors.gray[50]};
`;

const ModuleCard = styled.div`
  background: white;
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  padding: ${adminTheme.spacing.md};
  margin-bottom: ${adminTheme.spacing.md};
`;

const ModuleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${adminTheme.spacing.sm};
`;

const LessonList = styled.div`
  padding-left: ${adminTheme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
`;

const LessonItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${adminTheme.spacing.sm};
  background: ${adminTheme.colors.gray[50]};
  border-radius: ${adminTheme.borderRadius.md};
`;

const SmallButton = styled.button`
  padding: ${adminTheme.spacing.xs} ${adminTheme.spacing.sm};
  border-radius: ${adminTheme.borderRadius.md};
  font-size: ${adminTheme.fontSizes.xs};
  font-weight: ${adminTheme.fontWeights.semibold};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: white;
  color: ${adminTheme.colors.gray[700]};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
  }
`;

const RemoveButton = styled(SmallButton)`
  background: ${adminTheme.colors.danger[500]};
  border-color: ${adminTheme.colors.danger[500]};
  color: white;

  &:hover {
    background: ${adminTheme.colors.danger[600]};
  }
`;

const EditButton = styled(SmallButton)`
  background: ${adminTheme.colors.primary[500]};
  border-color: ${adminTheme.colors.primary[500]};
  color: white;

  &:hover {
    background: ${adminTheme.colors.primary[600]};
  }
`;

// Array Input Components (for skills, prerequisites, outcomes)
const ArrayInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
`;

const ArrayItemRow = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.sm};
  align-items: center;
`;

function PageBody() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [editingSkillIndex, setEditingSkillIndex] = useState(null);
  const [editingOutcomeIndex, setEditingOutcomeIndex] = useState(null);
  const toast = useToast();

  const getInitialForm = () => ({
    title: '',
    slug: '',
    description: {
      short: '',
      full: ''
    },
    category: '',
    duration: '',
    price: 0,
    curriculum: [],
    skills: [],
    learningOutcomes: [],
    instructor: {
      name: '',
      bio: '',
      image: ''
    },
    rating: {
      average: 0,
      count: 0
    },
    enrollmentCount: 0,
    thumbnail: '',
    featured: false,
    new: false,
    order: 0
  });

  const [editForm, setEditForm] = useState(getInitialForm());

  const load = useCallback(async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh) {
      const cached = cache.get('courses-list-admin');
      if (cached) {
        setItems(cached);
        return;
      }
    }

    setLoading(true);
    try {
      // Include all courses (even inactive ones) for admin panel
      const res = await fetch(`${API_BASE_URL}/courses?includeAll=true`);
      const data = await res.json();
      const courses = data.data?.courses || [];
      setItems(courses);
      // Cache for 5 minutes
      cache.set('courses-list-admin', courses);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setIsCreating(true);
    setEditing(null);
    setEditForm(getInitialForm());
    setActiveTab('basic');
  };

  const openEdit = (course) => {
    setIsCreating(false);
    setEditing(course);
    setEditForm({
      title: course.title || '',
      slug: course.slug || '',
      description: {
        short: course.shortDescription || course.description?.short || '',
        full: course.fullDescription || course.description?.full || ''
      },
      category: course.category || '',
      duration: course.duration || '',
      price: parseFloat(course.price) || 0,
      curriculum: course.curriculum || [],
      skills: course.skills || [],
      learningOutcomes: course.learningOutcomes || [],
      instructor: {
        name: course.instructor?.name || '',
        bio: course.instructor?.bio || '',
        image: course.instructor?.image || ''
      },
      rating: {
        average: parseFloat(course.rating) || course.rating?.average || 0,
        count: course.rating?.count || 0
      },
      enrollmentCount: course.totalEnrolled || course.enrollmentCount || 0,
      thumbnail: course.image || course.thumbnail || '',
      featured: course.featured || false,
      new: course.newCourse || course.new || false,
      order: course.order || 0
    });
    setActiveTab('basic');
  };

  const closeModal = () => {
    setIsCreating(false);
    setEditing(null);
    setEditForm(getInitialForm());
    setActiveTab('basic');
  };

  const saveCourse = async () => {
    try {
      const url = isCreating
        ? `${API_BASE_URL}/courses`
        : `${API_BASE_URL}/courses/${editing._id}`;
      
      const method = isCreating ? 'POST' : 'PUT';

      // Simplified course data for new CourseSimple model
      const courseData = {
        title: editForm.title.trim(),
        slug: editForm.slug.trim(), // Auto-generated on backend if not provided
        shortDescription: editForm.description.short.trim(),
        fullDescription: editForm.description.full.trim(),
        category: editForm.category.trim(),
        duration: editForm.duration.trim(),
        price: parseFloat(editForm.price) || 0,
        image: editForm.thumbnail.trim(),
        curriculum: editForm.curriculum,
        instructor: {
          name: editForm.instructor.name.trim() || 'Service Hub',
          bio: editForm.instructor.bio.trim(),
          image: editForm.instructor.image.trim()
        },
        skills: editForm.skills,
        learningOutcomes: editForm.learningOutcomes,
        rating: parseFloat(editForm.rating.average) || 0,
        totalEnrolled: parseInt(editForm.enrollmentCount) || 0,
        newCourse: editForm.new || false,
        featured: editForm.featured || false,
        order: parseInt(editForm.order) || 0
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(courseData)
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        const errorMsg = data.message || data.error || 'Operation failed';
        const detailedError = data.errors ? `\n${data.errors.map(e => `- ${e.field || e.path}: ${e.message}`).join('\n')}` : '';
        throw new Error(errorMsg + detailedError);
      }

      toast.success(isCreating ? 'Course created! 🎉' : 'Course updated! ✨');
      closeModal();
      // Invalidate cache and reload
      cache.invalidate('courses-list-admin');
      load(true);
    } catch (error) {
      toast.error(error.message || 'Failed to save course');
    }
  };

  const onDelete = async (id) => {
    setConfirmId(id);
  };

  const confirmDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/courses/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('delete failed');
      toast.success('Course deleted');
      // Invalidate cache and reload
      cache.invalidate('courses-list-admin');
      load(true);
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  // Curriculum management functions
  const addModule = () => {
    setEditForm({
      ...editForm,
      curriculum: [...editForm.curriculum, { title: '', lessons: [] }]
    });
  };

  const updateModule = (index, field, value) => {
    const newCurriculum = [...editForm.curriculum];
    newCurriculum[index] = { ...newCurriculum[index], [field]: value };
    setEditForm({ ...editForm, curriculum: newCurriculum });
  };

  const removeModule = (index) => {
    setEditForm({
      ...editForm,
      curriculum: editForm.curriculum.filter((_, i) => i !== index)
    });
  };

  const addLesson = (moduleIndex) => {
    const newCurriculum = [...editForm.curriculum];
    newCurriculum[moduleIndex].lessons.push({ title: '', duration: '' });
    setEditForm({ ...editForm, curriculum: newCurriculum });
  };

  const updateLesson = (moduleIndex, lessonIndex, field, value) => {
    const newCurriculum = [...editForm.curriculum];
    newCurriculum[moduleIndex].lessons[lessonIndex][field] = value;
    setEditForm({ ...editForm, curriculum: newCurriculum });
  };

  const removeLesson = (moduleIndex, lessonIndex) => {
    const newCurriculum = [...editForm.curriculum];
    newCurriculum[moduleIndex].lessons = newCurriculum[moduleIndex].lessons.filter((_, i) => i !== lessonIndex);
    setEditForm({ ...editForm, curriculum: newCurriculum });
  };

  // Array field management (skills, prerequisites, outcomes)
  const addArrayItem = (field, value) => {
    if (value.trim()) {
      setEditForm({
        ...editForm,
        [field]: [...editForm[field], value.trim()]
      });
    }
  };

  const removeArrayItem = (field, index) => {
    // Reset editing state if we're removing the item being edited
    if (field === 'skills' && editingSkillIndex === index) {
      setEditingSkillIndex(null);
    } else if (field === 'skills' && editingSkillIndex > index) {
      // Adjust index if we're removing an item before the one being edited
      setEditingSkillIndex(editingSkillIndex - 1);
    }
    
    if (field === 'learningOutcomes' && editingOutcomeIndex === index) {
      setEditingOutcomeIndex(null);
    } else if (field === 'learningOutcomes' && editingOutcomeIndex > index) {
      // Adjust index if we're removing an item before the one being edited
      setEditingOutcomeIndex(editingOutcomeIndex - 1);
    }
    
    setEditForm({
      ...editForm,
      [field]: editForm[field].filter((_, i) => i !== index)
    });
  };

  const updateArrayItem = (field, index, value) => {
    const newArray = [...editForm[field]];
    newArray[index] = value;
    setEditForm({
      ...editForm,
      [field]: newArray
    });
  };

  const finishEditingArrayItem = (field, index, setEditingIndex) => {
    // Validate index is within bounds
    if (index < 0 || index >= editForm[field].length) {
      setEditingIndex(null);
      return;
    }
    // Validate that the value is not empty or whitespace-only
    const value = editForm[field][index];
    if (!value || typeof value !== 'string' || !value.trim()) {
      toast.error('Value cannot be empty');
      return;
    }
    // Trim whitespace and update
    updateArrayItem(field, index, value.trim());
    setEditingIndex(null);
  };

  return (
    <div>
      <PageHeader>
        <Title>
          <span>🎓</span>
          Course Management
        </Title>
        <CreateButton onClick={openCreate}>
          <span>➕</span>
          Create New Course
        </CreateButton>
      </PageHeader>

      <TableWrapper>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner label="Loading courses..." />
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'title', header: 'Title', width: '2fr' },
              { key: 'category', header: 'Category', width: '1fr' },
              { key: 'level', header: 'Level', width: '0.8fr' },
              { key: 'duration', header: 'Duration', width: '0.8fr' },
              {
                key: 'price',
                header: 'Price',
                width: '0.8fr',
                render: (c) => `$${c.price?.discounted || c.price?.original || 0}`
              },
            ]}
            rows={items}
            actions={(c) => (
              <>
                <ActionButton onClick={() => openEdit(c)}>✏️ Edit</ActionButton>
                <DeleteButton onClick={() => onDelete(c._id)}>🗑️ Delete</DeleteButton>
              </>
            )}
          />
        )}
      </TableWrapper>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={confirmDelete}
        title="Delete course?"
        description="This action cannot be undone."
      />

      <Modal
        open={isCreating || !!editing}
        title={isCreating ? '✨ Create New Course' : '✏️ Edit Course'}
        onCancel={closeModal}
        onSave={saveCourse}
        saveLabel={isCreating ? 'Create Course' : 'Save Changes'}
      >
        <TabNav>
          <Tab active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>
            📋 Basic Info
          </Tab>
          <Tab active={activeTab === 'content'} onClick={() => setActiveTab('content')}>
            📚 Content & Curriculum
          </Tab>
          <Tab active={activeTab === 'instructor'} onClick={() => setActiveTab('instructor')}>
            👨‍🏫 Instructor
          </Tab>
          <Tab active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
            ⚙️ Settings
          </Tab>
        </TabNav>

        <ModalForm>
          {/* BASIC INFO TAB */}
          {activeTab === 'basic' && (
            <>
              <TwoColumn>
                <FormGroup>
                  <Label>Course Title *</Label>
                  <Input
                    placeholder="Complete Web Development Bootcamp"
                    value={editForm.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      const slug = title
                        .toLowerCase()
                        .trim()
                        .replace(/[^\w\s-]/g, '')
                        .replace(/[\s_-]+/g, '-')
                        .replace(/^-+|-+$/g, '');
                      setEditForm({ ...editForm, title, slug });
                    }}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Slug (Auto-generated)</Label>
                  <Input
                    placeholder="complete-web-development-bootcamp"
                    value={editForm.slug}
                    onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </FormGroup>
              </TwoColumn>

              <FormGroup>
                <Label>Short Description * (max 200 chars)</Label>
                <TextArea
                  placeholder="A brief overview of the course..."
                  value={editForm.description.short}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    description: { ...editForm.description, short: e.target.value }
                  })}
                  maxLength={200}
                  rows={2}
                  required
                />
                <small style={{ color: adminTheme.colors.gray[600], fontSize: adminTheme.fontSizes.xs }}>
                  {editForm.description.short.length}/200 characters
                </small>
              </FormGroup>

              <FormGroup>
                <Label>Course Description *</Label>
                <TextArea
                  placeholder="Detailed course description..."
                  value={editForm.description.full}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    description: { ...editForm.description, full: e.target.value }
                  })}
                  rows={6}
                  required
                />
              </FormGroup>

              <TwoColumn>
                <FormGroup>
                  <Label>Category (Optional)</Label>
                  <Input
                    placeholder="e.g., Programming, Design, etc."
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Duration *</Label>
                  <Input
                    placeholder="e.g., 6 months, 4 weeks"
                    value={editForm.duration}
                    onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                    required
                  />
                </FormGroup>
              </TwoColumn>

              <FormGroup>
                <Label>Price (₹) - Set 0 for "Contact on WhatsApp"</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g., 25000 or 0 for contact"
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                />
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  💡 If price is 0, course will show "Contact on WhatsApp" button
                </small>
              </FormGroup>

              <FormGroup>
                <ImageUpload
                  label="Course Thumbnail"
                  value={editForm.thumbnail}
                  onChange={(url) => setEditForm({ ...editForm, thumbnail: url })}
                  folder="courses"
                />
              </FormGroup>
            </>
          )}

          {/* CONTENT & CURRICULUM TAB */}
          {activeTab === 'content' && (
            <>
              <FormGroup>
                <Label>Curriculum</Label>
                <CurriculumBuilder>
                  {editForm.curriculum.map((module, moduleIdx) => (
                    <ModuleCard key={moduleIdx}>
                      <ModuleHeader>
                        <Input
                          placeholder="Module Title"
                          value={module.title}
                          onChange={(e) => updateModule(moduleIdx, 'title', e.target.value)}
                          style={{ marginBottom: adminTheme.spacing.sm }}
                        />
                        <RemoveButton onClick={() => removeModule(moduleIdx)}>
                          Remove Module
                        </RemoveButton>
                      </ModuleHeader>
                      
                      <LessonList>
                        {module.lessons?.map((lesson, lessonIdx) => (
                          <LessonItem key={lessonIdx}>
                            <Input
                              placeholder="Lesson Title"
                              value={lesson.title}
                              onChange={(e) => updateLesson(moduleIdx, lessonIdx, 'title', e.target.value)}
                              style={{ flex: 2, marginRight: adminTheme.spacing.sm }}
                            />
                            <Input
                              placeholder="Duration"
                              value={lesson.duration}
                              onChange={(e) => updateLesson(moduleIdx, lessonIdx, 'duration', e.target.value)}
                              style={{ flex: 1, marginRight: adminTheme.spacing.sm }}
                            />
                            <RemoveButton onClick={() => removeLesson(moduleIdx, lessonIdx)}>
                              ×
                            </RemoveButton>
                          </LessonItem>
                        ))}
                        <SmallButton onClick={() => addLesson(moduleIdx)}>
                          + Add Lesson
                        </SmallButton>
                      </LessonList>
                    </ModuleCard>
                  ))}
                  <SmallButton onClick={addModule}>+ Add Module</SmallButton>
                </CurriculumBuilder>
              </FormGroup>

              {/* Skills */}
              <FormGroup>
                <Label> Skills/Stack (What students will learn)</Label>
                <ArrayInput>
                  {editForm.skills.map((skill, idx) => (
                    <ArrayItemRow key={idx}>
                      <Input 
                        value={skill} 
                        readOnly={editingSkillIndex !== idx}
                        onChange={(e) => updateArrayItem('skills', idx, e.target.value)}
                        style={{ flex: 1 }} 
                      />
                      {editingSkillIndex === idx ? (
                        <EditButton 
                          onClick={() => finishEditingArrayItem('skills', idx, setEditingSkillIndex)}
                          aria-label="Save skill changes"
                        >
                          ✓
                        </EditButton>
                      ) : (
                        <EditButton 
                          onClick={() => setEditingSkillIndex(idx)}
                          aria-label="Edit skill"
                        >
                          ✏️
                        </EditButton>
                      )}
                      <RemoveButton 
                        onClick={() => removeArrayItem('skills', idx)}
                        aria-label="Remove skill"
                      >
                        ×
                      </RemoveButton>
                    </ArrayItemRow>
                  ))}
                  <ArrayItemRow>
                    <Input
                      placeholder="Add a skill..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addArrayItem('skills', e.target.value);
                          e.target.value = '';
                        }
                      }}
                      style={{ flex: 1 }}
                    />
                    <SmallButton onClick={(e) => {
                      const input = e.target.previousSibling;
                      addArrayItem('skills', input.value);
                      input.value = '';
                    }}>
                      + Add
                    </SmallButton>
                  </ArrayItemRow>
                </ArrayInput>
              </FormGroup>

              {/* Learning Outcomes */}
              <FormGroup>
                <Label>Learning Objectives / What You will You Learn</Label>
                <ArrayInput>
                  {editForm.learningOutcomes.map((outcome, idx) => (
                    <ArrayItemRow key={idx}>
                      <Input 
                        value={outcome} 
                        readOnly={editingOutcomeIndex !== idx}
                        onChange={(e) => updateArrayItem('learningOutcomes', idx, e.target.value)}
                        style={{ flex: 1 }} 
                      />
                      {editingOutcomeIndex === idx ? (
                        <EditButton 
                          onClick={() => finishEditingArrayItem('learningOutcomes', idx, setEditingOutcomeIndex)}
                          aria-label="Save learning outcome changes"
                        >
                          ✓
                        </EditButton>
                      ) : (
                        <EditButton 
                          onClick={() => setEditingOutcomeIndex(idx)}
                          aria-label="Edit learning outcome"
                        >
                          ✏️
                        </EditButton>
                      )}
                      <RemoveButton 
                        onClick={() => removeArrayItem('learningOutcomes', idx)}
                        aria-label="Remove learning outcome"
                      >
                        ×
                      </RemoveButton>
                    </ArrayItemRow>
                  ))}
                  <ArrayItemRow>
                    <Input
                      placeholder="Add a learning outcome..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addArrayItem('learningOutcomes', e.target.value);
                          e.target.value = '';
                        }
                      }}
                      style={{ flex: 1 }}
                    />
                    <SmallButton onClick={(e) => {
                      const input = e.target.previousSibling;
                      addArrayItem('learningOutcomes', input.value);
                      input.value = '';
                    }}>
                      + Add
                    </SmallButton>
                  </ArrayItemRow>
                </ArrayInput>
              </FormGroup>
            </>
          )}

          {/* INSTRUCTOR TAB */}
          {activeTab === 'instructor' && (
            <>
              <FormGroup>
                <Label>Instructor Name</Label>
                <Input
                  placeholder="Jane Doe"
                  value={editForm.instructor.name}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    instructor: { ...editForm.instructor, name: e.target.value }
                  })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Instructor Bio</Label>
                <TextArea
                  placeholder="Instructor background and expertise..."
                  value={editForm.instructor.bio}
                  onChange={(e) => setEditForm({ 
                    ...editForm, 
                    instructor: { ...editForm.instructor, bio: e.target.value }
                  })}
                  rows={4}
                />
              </FormGroup>

              <FormGroup>
                <ImageUpload
                  label="Instructor Image"
                  value={editForm.instructor.image}
                  onChange={(url) => setEditForm({ 
                    ...editForm, 
                    instructor: { ...editForm.instructor, image: url }
                  })}
                  folder="instructors"
                />
              </FormGroup>
            </>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <>
              <ThreeColumn>
                <FormGroup>
                  <Label>Rating (0-5)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={editForm.rating.average}
                    onChange={(e) => setEditForm({ 
                      ...editForm, 
                      rating: { ...editForm.rating, average: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Total Enrolled</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.enrollmentCount}
                    onChange={(e) => setEditForm({ ...editForm, enrollmentCount: parseInt(e.target.value) || 0 })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.order}
                    onChange={(e) => setEditForm({ ...editForm, order: parseInt(e.target.value) || 0 })}
                  />
                  <small style={{ color: adminTheme.colors.gray[600], fontSize: adminTheme.fontSizes.xs, marginTop: '4px', display: 'block' }}>
                    Lower numbers appear first on the frontend
                  </small>
                </FormGroup>
              </ThreeColumn>

              <TwoColumn>
                <CheckboxGroup>
                  <input
                    type="checkbox"
                    checked={editForm.featured}
                    onChange={(e) => setEditForm({ ...editForm, featured: e.target.checked })}
                    id="featured-course"
                  />
                  <Label htmlFor="featured-course">⭐ Featured Course</Label>
                </CheckboxGroup>

                <CheckboxGroup>
                  <input
                    type="checkbox"
                    checked={editForm.new}
                    onChange={(e) => setEditForm({ ...editForm, new: e.target.checked })}
                    id="new-course"
                  />
                  <Label htmlFor="new-course">✨ New Course</Label>
                </CheckboxGroup>
              </TwoColumn>
            </>
          )}
        </ModalForm>
      </Modal>
    </div>
  );
}

export default function AdminCourses() {
  return (
    <ToastProvider>
      <PageBody />
    </ToastProvider>
  );
}
